/**
 * TRANSICIÓN DE CACHÉ LEGACY (ANTES DE FASE 5.6) — §23/§24
 *
 * Hasta 5.5, la base offline se llamaba `neatos-offline-{idUsuario}` y
 * guardaba todo en claro. Desde 5.6, la base física es
 * `neatos-offline-cripto-{perfilId}` (`perfil.ts`) — un nombre distinto a
 * propósito, nunca la misma base "actualizada en sitio": así es imposible
 * que quede una ruta de lectura que caiga de vuelta al plaintext antiguo
 * por error (§24 — "no crear 'si cifrado falla, lee el antiguo'").
 *
 * Este módulo es la ÚNICA razón por la que el nombre legacy sigue
 * apareciendo en el código: detectar si existe, decidir si puede
 * limpiarse sola, y limpiarla explícitamente cuando el usuario lo pide.
 * Nunca la abre para OPERAR con sus datos — sólo para contar mutaciones
 * sin resolver y, si procede, borrarla entera.
 *
 * SIMPLIFICACIÓN DELIBERADA (§23: "FASE 5.6 se desarrolla antes de
 * producción real y actualmente se usan datos sintéticos")
 *
 * No se intenta un reenvío automático de las mutaciones legacy pendientes
 * contra `/api/sync` desde aquí — duplicaría la máquina de sincronización
 * de `offline/sync.ts` contra un almacén con una forma de fila distinta
 * (sin cifrar, sin `perfilId`), fuente de errores para un caso que hoy no
 * tiene datos reales detrás. Si aparecen mutaciones sin resolver, la única
 * salida que ofrece este módulo es un descarte EXPLÍCITO (§23: "...o
 * descartarlos explícitamente"), nunca automático ni silencioso — ver
 * `descartarLegado`.
 */

const PREFIJO_LEGACY = 'neatos-offline-';

export interface EstadoLegado {
  /** `false` = no hay nada que migrar; el resto de campos no aplica. */
  readonly existe: boolean;
  /** Mutaciones con `estadoSync !== 'SINCRONIZADA'` en la base legacy — lo único que impide una limpieza silenciosa. */
  readonly mutacionesSinResolver: number;
}

async function nombreLegacyExiste(idUsuario: string): Promise<boolean> {
  //  `indexedDB.databases()` no existe en todos los entornos (algunos
  //  navegadores antiguos, algunos runtimes de prueba) — mejora
  //  progresiva, igual criterio que el resto de este código offline ante
  //  una API ausente: si no se puede comprobar, se asume que no hay nada
  //  legacy que limpiar, nunca se bloquea la protección del dispositivo
  //  por una API opcional.
  if (typeof indexedDB.databases !== 'function') return false;
  const bases = await indexedDB.databases();
  return bases.some((b) => b.name === `${PREFIJO_LEGACY}${idUsuario}`);
}

/**
 * Comprueba si existe una base legacy de este usuario y, si existe,
 * cuántas mutaciones sin resolver contiene. Nunca modifica nada.
 */
export async function comprobarLegado(idUsuario: string): Promise<EstadoLegado> {
  if (!(await nombreLegacyExiste(idUsuario))) return { existe: false, mutacionesSinResolver: 0 };

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const peticion = indexedDB.open(`${PREFIJO_LEGACY}${idUsuario}`);
    peticion.onsuccess = () => resolve(peticion.result);
    peticion.onerror = () => reject(peticion.error ?? new Error('No se ha podido abrir la caché anterior de este dispositivo.'));
  });
  try {
    if (!db.objectStoreNames.contains('mutaciones')) return { existe: true, mutacionesSinResolver: 0 };
    const tx = db.transaction('mutaciones', 'readonly');
    const todas = await new Promise<{ readonly estadoSync?: string }[]>((resolve, reject) => {
      const peticion = tx.objectStore('mutaciones').getAll();
      peticion.onsuccess = () => resolve(peticion.result as { readonly estadoSync?: string }[]);
      peticion.onerror = () => reject(peticion.error ?? new Error('No se ha podido leer la caché anterior.'));
    });
    const sinResolver = todas.filter((m) => m.estadoSync !== 'SINCRONIZADA').length;
    return { existe: true, mutacionesSinResolver: sinResolver };
  } finally {
    db.close();
  }
}

/**
 * Elimina la base legacy entera. Se llama en dos casos, ambos explícitos:
 *
 *   1. automáticamente, sólo cuando `comprobarLegado` ya confirmó CERO
 *      mutaciones sin resolver (nada que perder);
 *   2. tras que el usuario confirme EXPLÍCITAMENTE "descartar cambios
 *      pendientes" en la pantalla de "Proteger este dispositivo" (§23),
 *      cuando sí había mutaciones sin resolver.
 *
 * Nunca se llama en silencio si `mutacionesSinResolver > 0` sin que medie
 * esa confirmación — es responsabilidad de quien llama, no de esta
 * función, que no vuelve a comprobar el conteo.
 */
export async function descartarLegado(idUsuario: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const peticion = indexedDB.deleteDatabase(`${PREFIJO_LEGACY}${idUsuario}`);
    peticion.onsuccess = () => resolve();
    peticion.onerror = () => reject(peticion.error ?? new Error('No se ha podido eliminar la caché anterior.'));
    peticion.onblocked = () => resolve(); // otra pestaña la tiene abierta — se completará cuando la cierre.
  });
}
